import React, { Fragment, useEffect, useState } from "react";
import { ShowHide } from "components/layout";
import { AlertUnsavedData, NotificationToaster } from "components/notifications";
import { UserAccess, UserHasAccess } from "components/user-access";
import { useDirtyData } from "hooks/useDirtyData";
import { useValidation } from "hooks/useValidation";
import { PageRestricted } from "pages/errors/page-restricted";
import PropTypes from "prop-types";
import { DefaultEventTypeProcessModel, DefaultEventTypes, DefaultEventTypeScheduleModel, DefaultVorRequiredOptions, FailedVorRequiredOption } from "pages/fleet/fleet-event-type/wizard/shared/DefaultEventTypeModel";
import { FleetService } from "services";
import { Intent } from "@blueprintjs/core";
import { FormHelperText, FormNumericInput, FormRadio, FormSelect } from "components/form-fields";
import { ButtonSave } from "components/buttons";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import "./EventTypeSchedule.css";

export function EventTypeSchedule({
    model = DefaultEventTypeScheduleModel,
    forceLoad = false,
    processModel = DefaultEventTypeProcessModel,
    isInWizardMode = true,
    saving = false,
    globalLoading = false,
    onValidationUpdate = () => { },
    onModelUpdate = () => { return DefaultEventTypeScheduleModel; },
    onSave = () => { },
    onForceLoadFinished = () => { },
    specialProcessingKey = null
}) {
    var navigate = useNavigate();

    const step = 4;
    const fmsManageAction = "FMS:Manage";
    const userHasFmsManageAction = UserHasAccess(fmsManageAction);
    
    const [loading, setLoading] = useState(true);
    const [initialLoadCompleted, setInitialLoadCompleted] = useState(false);
    const [optionsFetched, setOptionsFetched] = useState(false);

    const [originalScheduleModel, setOriginalScheduleModel] = useState(DefaultEventTypeScheduleModel);
    const [scheduleModel, setScheduleModel] = useState(DefaultEventTypeScheduleModel);

    const [isValid, scheduleModelErrors, validateScheduleModel] = useValidation();
    const isScheduleModelDirty = useDirtyData(originalScheduleModel, scheduleModel);

    const [frequencyOptions, setFrequencyOptions] = useState([])
    const [vorRequiredOptions, setVorRequiredOptions] = useState([]);

    const [eventTypes, setEventTypes] = useState([]);
    const [selectedEventTypeId, setSelectedEventTypeId] = useState(null);
    const [lockSchedule, setLockSchedule] = useState(false)
    
    // Field names
    const adHocFieldName = "adHoc";
    const repeatFrequencyFieldName = "repeatFrequency";
    const repeatIntervalFieldName = "repeatInterval";
    const repeatCountFieldName = "repeatCount";
    const vorTimerValueFieldName = "vorTimerValue";
    const vorTimerFieldName = "vorTimer";
    const vorTimerFailedOnlyFieldName = "vorTimerFailedOnly";
    const vorPreDaysFieldName = "vorPreDays";
    const vorPostDaysFieldName = "vorPostDays";
    // Field names END

    const numericInputsSelectFieldNames = [repeatIntervalFieldName, repeatCountFieldName, vorPreDaysFieldName, vorPostDaysFieldName];
    const enumeratedSelectFieldNames = [repeatFrequencyFieldName];
    const switchFieldNames = [vorTimerFieldName];

    useEffect(() => {
        if (!userHasFmsManageAction || (forceLoad === false && initialLoadCompleted)) {
            return;
        }

        setModel(prev => { return {...prev, ...model}; });

        if (model.adHoc !== null && model.adHoc !== undefined) {
            const eventType = DefaultEventTypes.find(x => x.isAdHoc === model.adHoc);
            setSelectedEventTypeId(eventType.id);
        }

        setEventTypes(DefaultEventTypes);

        if (
            !isInWizardMode &&
            (
                model.adHoc !== DefaultEventTypeScheduleModel.adHoc || 
                model.repeatFrequency !== DefaultEventTypeScheduleModel.repeatFrequency ||
                model.repeatInterval !== DefaultEventTypeScheduleModel.repeatInterval ||
                model.repeatCount !== DefaultEventTypeScheduleModel.repeatCount ||
                model.vorTimerValue !== DefaultEventTypeScheduleModel.vorTimerValue ||
                model.vorTimer !== DefaultEventTypeScheduleModel.vorTimer ||
                model.vorTimerFailedOnly !== DefaultEventTypeScheduleModel.vorTimerFailedOnly ||
                model.vorPreDays !== DefaultEventTypeScheduleModel.vorPreDays ||
                model.vorPostDays !== DefaultEventTypeScheduleModel.vorPostDays
            )
        ) {
            setInitialLoadCompleted(true);
            setOriginalScheduleModel(model);
        } 
        else if (isInWizardMode === true) {
            setInitialLoadCompleted(true);
        }

        fetchOptions();

        if (forceLoad) {
            onForceLoadFinished();
        }
    }, [model, forceLoad]);

    function fetchOptions() {
        if (optionsFetched) {
            return;
        }

        setLoading(true);

        FleetService.getEventOptions()
            .then((response) => {
                setOptionsFetched(true);
                setFrequencyOptions(response.eventFrequency.map(o => ({ id: o.key.toLowerCase(), name: o.name })));
            })
            .catch(() => {
                NotificationToaster.show(Intent.WARNING, 'Event options could not be loaded. Please try again later.');
            })
            .finally(() => {
                setLoading(false);
            });
    }

    useEffect(() => {
        setLoading(globalLoading);
    }, [globalLoading]);

    // publish out validation changes if in wizard mode
    useEffect(() => {
        if (!isInWizardMode) {
            return;
        }

        onValidationUpdate(step, isValid);
    }, [isValid])

    useEffect(() => {
        if (specialProcessingKey && specialProcessingKey.toLowerCase() !== "pmi") {
            setLockSchedule(true);
        }
    }, [specialProcessingKey]);

    useEffect(validate, [scheduleModel])

    function validate() {
        let validationRules = [
            { fieldName: adHocFieldName, required: true },
            { fieldName: repeatCountFieldName, required: scheduleModel.adHoc === false, minValue: 1, maxValue: 50, type: 'number' },
            { fieldName: repeatFrequencyFieldName, required: scheduleModel.adHoc === false },
            { fieldName: repeatIntervalFieldName, required: scheduleModel.adHoc === false, minValue: 1, maxValue: 1000, type: 'number' },
            { fieldName: vorPreDaysFieldName, required: areVorFieldsVisible(), minValue: 0, maxValue: 50, type: 'number' },
            { fieldName: vorPostDaysFieldName, required: areVorFieldsVisible(), minValue: 0, maxValue: 50, type: 'number' }
        ];

        validateScheduleModel(validationRules, scheduleModel);
    }

    useEffect(() => {
        const tempVorRequiredOptions = [...DefaultVorRequiredOptions];

        if (processModel.failStatusEnabled) {
            tempVorRequiredOptions.push(FailedVorRequiredOption);
        }

        setVorRequiredOptions(tempVorRequiredOptions);

    }, [processModel.failStatusEnabled])

    function onEventTypeChange(eventType) {
        if (loading) {
            return;
        }
        
        setSelectedEventTypeId(eventType.id);

        const clonedModel = {
            ...scheduleModel,
            adHoc: eventType.isAdHoc
        };

        setScheduleModel(clonedModel);
        onModelUpdate(step, clonedModel);
    }

    function onRequiresVorValueChange(item) {
        const selectedValue = vorRequiredOptions.find(option => option.value === item.currentTarget.value);

        const clonedModel = {
            ...scheduleModel,
            [vorTimerFieldName]: selectedValue.vorRequired,
            [vorTimerValueFieldName]: selectedValue.value,
            [vorTimerFailedOnlyFieldName]: selectedValue.vorRequiredFailedOnly
        };

        setScheduleModel(clonedModel);
        onModelUpdate(step, clonedModel);
    }

    // Use this to handle input value changes
    function onValueChange(fieldName, item) {
        let value = item;

        if (enumeratedSelectFieldNames.includes(fieldName)) {
            value = item.id;
        }
        else if (switchFieldNames.includes(fieldName)) {
            value = item.currentTarget.checked;
        } 
        else if (numericInputsSelectFieldNames.includes(fieldName)) {
            value = value === "" ? null : Number(value);
        }

        const clonedModel = {
            ...scheduleModel,
            [fieldName]: value
        };

        setScheduleModel(clonedModel);  
        onModelUpdate(step, clonedModel);
    }


    function setModel(changeModel) {
        setOriginalScheduleModel(changeModel);
        setScheduleModel(changeModel);
    }

    function areVorFieldsVisible() {
        return scheduleModel.vorTimerValue === "2" || scheduleModel.vorTimerValue === "3";
    }

    function showScheduleFields() {
        return scheduleModel.adHoc === false;
    }

    function onConfirmLeave(redirectToPath) {
        setModel(originalScheduleModel);
        onModelUpdate(step, originalScheduleModel);

        setTimeout(() => navigate(redirectToPath));
    }

    function fieldHasError(fieldName) {
        return scheduleModelErrors[fieldName] !== null && scheduleModelErrors[fieldName] !== undefined && scheduleModelErrors[fieldName] !== "";
    }

    return (
        <UserAccess perform={fmsManageAction}
            yes={() => (
                <Fragment>
                    <ShowHide
                        evaluator={!isInWizardMode}
                        show={(
                            <Fragment>
                                <h2>Event Schedule</h2>
                                <AlertUnsavedData isDirty={isScheduleModelDirty} onConfirmLeave={onConfirmLeave} />
                            </Fragment>
                        )}
                    />

                    <div className="spacer-top-small">
                        <FormSelect
                            disabled={saving || loading || lockSchedule}
                            loading={loading}
                            items={eventTypes}
                            onItemSelect={(item) => onEventTypeChange(item)}
                            placeholder="Please select"
                            selectedValue={selectedEventTypeId}
                            dangerHelperText={scheduleModelErrors["adHoc"]}
                            id="select-event-type"
                        />
                    </div>
                    <ShowHide
                        evaluator={showScheduleFields()}
                        show={(
                            <Fragment>
                                <div className="form-row field-combination spacer-bottom-small">
                                    <h4>Event frequency</h4>
                                    <FormNumericInput
                                        disabled={saving || lockSchedule}
                                        disabledInput={saving || lockSchedule}
                                        min={1}
                                        max={1000}
                                        selectedValue={scheduleModel.repeatInterval}
                                        onValueChange={(item) => onValueChange(repeatIntervalFieldName, item)}
                                        loading={loading}
                                    />
                                    <FormSelect
                                        disabled={saving || lockSchedule}
                                        items={frequencyOptions}
                                        onItemSelect={(item) => onValueChange(repeatFrequencyFieldName, item)}
                                        selectedValue={scheduleModel.repeatFrequency}
                                        loading={loading}
                                    />
                                </div>

                                <FormNumericInput
                                    disabled={saving || lockSchedule}
                                    disabledInput={saving || lockSchedule}
                                    min={1}
                                    max={50}
                                    selectedValue={scheduleModel.repeatCount}
                                    onValueChange={(item) => onValueChange(repeatCountFieldName, item)}
                                    loading={loading}
                                    headingText="Events in advance (rolling)"
                                    helperText="Please select the number of events in advance you would like to be added to the Asset page. The dates of these planned events will move in relation to the completion date of the incoming events."
                                    dangerHelperText={scheduleModelErrors[repeatCountFieldName]}
                                />

                                <FormRadio
                                    disabled={saving}
                                    options={vorRequiredOptions}
                                    onChange={(item) => onRequiresVorValueChange(item)}
                                    headingText="Enable vehicle off road timer"
                                    selectedValue={scheduleModel.vorTimerValue}
                                    dangerHelperText={scheduleModelErrors[vorTimerValueFieldName]}
                                    loading={loading}
                                    id="input-vor-timer"
                                />

                                <ShowHide
                                    evaluator={areVorFieldsVisible()}
                                    show={(
                                        <Fragment>
                                            <div className="form-row spacer-bottom-small">
                                                <h4>Days before event scheduled date</h4>
                                                <div className="inline-items">
                                                    <FormNumericInput
                                                        disabled={saving}
                                                        min={0}
                                                        max={50}
                                                        selectedValue={scheduleModel.vorPreDays}
                                                        onValueChange={(item) => onValueChange(vorPreDaysFieldName, item)}
                                                        loading={loading}
                                                    />
                                                    <div className="event-type-schedule-vor-suffix">Day(s)</div>
                                                </div>
                                                <div className={classNames("", {"helper-text": fieldHasError(vorPreDaysFieldName)})}>
                                                    <FormHelperText loading={loading} danger={true}>{scheduleModelErrors[vorPreDaysFieldName]}</FormHelperText>
                                                </div>
                                            </div>

                                            <div className="form-row spacer-bottom-small">
                                                <h4>Days after event completion date</h4>
                                                <div className="inline-items">
                                                    <FormNumericInput
                                                        disabled={saving}
                                                        min={0}
                                                        max={50}
                                                        selectedValue={scheduleModel.vorPostDays}
                                                        onValueChange={(item) => onValueChange(vorPostDaysFieldName, item)}
                                                        loading={loading}
                                                    />
                                                    <div className="event-type-schedule-vor-suffix">Day(s)</div>
                                                </div>
                                                <div className={classNames("", {"helper-text": fieldHasError(vorPostDaysFieldName)})}>
                                                    <FormHelperText loading={loading} danger={true}>{scheduleModelErrors[vorPostDaysFieldName]}</FormHelperText>
                                                </div>
                                            </div>
                                        </Fragment>
                                    )}
                                />
                            </Fragment>
                        )}
                    />

                    <ShowHide
                        evaluator={!isInWizardMode}
                        show={(
                            <ButtonSave
                                loading={loading}
                                onClick={() => onSave("schedule")}
                                disabled={saving}
                                simpleDisabled={!isScheduleModelDirty || !isValid}
                            />	
                        )}
                    />
                </Fragment>
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

EventTypeSchedule.propTypes = {
    model: PropTypes.object,
    forceLoad: PropTypes.bool,
    processModel: PropTypes.object,
    isInWizardMode: PropTypes.bool,
    saving: PropTypes.bool.isRequired,
    globalLoading: PropTypes.bool,
    onValidationUpdate: PropTypes.func,
    onModelUpdate: PropTypes.func,
    onSave: PropTypes.func,
    onForceLoadFinished: PropTypes.func,
    specialProcessingKey: PropTypes.string
};
